import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, ListOrdered } from 'lucide-react';
import { Button } from '../components/ui/button';
import DataGrid from '../components/grid/DataGrid';
import Modal from '../components/ui/Modal';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { apiService } from '../services/api';
import { StatusDto, CreateStatusDto, UpdateStatusDto } from '../types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ConfirmDialog from '../components/modals/ConfirmDialog';

const schema = yup.object({
  name: yup.string().required('Name is required').max(100, 'Name must be less than 100 characters'),
  description: yup.string().max(500, 'Description must be less than 500 characters'),
  sortOrder: yup.number().required('Sort order is required').min(0, 'Sort order must be 0 or greater'),
  isActive: yup.boolean().required(),
  tabGroup: yup.string().nullable().oneOf([null, '', 'New Work','Active Work','On Hold','Pending','Complete','Closed'], 'Invalid Tab Group')
});

type FormData = yup.InferType<typeof schema>;

interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateStatusDto | UpdateStatusDto) => void;
  isLoading: boolean;
  status?: StatusDto | null;
}

const StatusModal: React.FC<StatusModalProps> = ({ isOpen, onClose, onSubmit, isLoading, status }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: status?.name || '',
      description: status?.description || '',
      sortOrder: status?.sortOrder || 0,
      isActive: status?.isActive ?? true,
      tabGroup: (status as any)?.tabGroup || 'none'
    }
  });

  React.useEffect(() => {
    if (status) {
      reset({
        name: status.name,
        description: status.description || '',
        sortOrder: status.sortOrder,
        isActive: status.isActive,
        tabGroup: (status as any)?.tabGroup || 'none'
      });
    } else {
      reset({
        name: '',
        description: '',
        sortOrder: 0,
        isActive: true,
        tabGroup: 'none'
      });
    }
  }, [status, reset]);

  const handleFormSubmit = (data: FormData) => {
    const payload: CreateStatusDto | UpdateStatusDto = {
      name: data.name,
      description: data.description,
      sortOrder: data.sortOrder,
      isActive: data.isActive,
      tabGroup: data.tabGroup && data.tabGroup !== 'none' && data.tabGroup !== null ? data.tabGroup : undefined
    };
    onSubmit(payload);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={status ? 'Edit Status' : 'Create Status'} maxWidth="md">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* Active checkbox at the top */}
        <div className="flex items-center space-x-2 pb-4 border-b border-gray-200">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Active
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter status name"
                className={errors.name ? 'border-red-500' : ''}
              />
            )}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Tab Group
          </label>
          <Controller
            name="tabGroup"
            control={control}
            render={({ field }) => (
              <Select value={(field.value as any) ?? 'none'} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="(None)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(None)</SelectItem>
                  <SelectItem value="New Work">New Work</SelectItem>
                  <SelectItem value="Active Work">Active Work</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Complete">Complete</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {(errors as any)?.tabGroup && (
            <p className="text-red-500 text-sm mt-1">{(errors as any).tabGroup.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter status description"
                className={errors.description ? 'border-red-500' : ''}
              />
            )}
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Sort Order <span className="text-red-500">*</span>
          </label>
          <Controller
            name="sortOrder"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                min="0"
                placeholder="Enter sort order"
                className={errors.sortOrder ? 'border-red-500' : ''}
                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
              />
            )}
          />
          {errors.sortOrder && (
            <p className="text-red-500 text-sm mt-1">{errors.sortOrder.message}</p>
          )}
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : status ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const StatusAdminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('sortOrder');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<StatusDto | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<StatusDto | null>(null);

  const { data: statusesResponse, isLoading, error } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses(),
  });

  const statuses = useMemo(() => {
    let all = statusesResponse?.data || [];
    
    // Apply search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      all = all.filter((s: StatusDto) =>
        s.name.toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q)
      );
    }
    
    // Apply sorting
    all = [...all].sort((a, b) => {
      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
      
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDirection === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? 1 : -1)
          : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      
      return 0;
    });
    
    return all;
  }, [statusesResponse, search, sortField, sortDirection]);

  // Calculate pagination
  const totalCount = statuses.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedStatuses = statuses.slice(startIndex, endIndex);

  const createMutation = useMutation({
    mutationFn: (data: CreateStatusDto) => apiService.createStatus(data),
    onSuccess: () => {
      toast.success('Status created successfully!');
      queryClient.invalidateQueries({ queryKey: ['statuses'] });
      setIsCreateModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(`Failed to create status: ${err.message || 'Unknown error'}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateStatusDto }) => apiService.updateStatus(id, data),
    onSuccess: () => {
      toast.success('Status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['statuses'] });
      setIsEditModalOpen(false);
      setEditingStatus(null);
    },
    onError: (err: any) => {
      toast.error(`Failed to update status: ${err.message || 'Unknown error'}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteStatus(id),
    onSuccess: () => {
      toast.success('Status deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['statuses'] });
      setIsDeleteConfirmOpen(false);
      setStatusToDelete(null);
    },
    onError: (err: any) => {
      toast.error(`Failed to delete status: ${err.message || 'Unknown error'}`);
    },
  });

  const handleDeleteClick = (status: StatusDto) => {
    setStatusToDelete(status);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (statusToDelete) {
      deleteMutation.mutate(statusToDelete.id);
    }
  };

  // Pagination handlers
  const handleSearch = (term: string) => {
    setSearch(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const columns = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (_value: any, row: StatusDto) => (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.name}</div>
      )
    },
    {
      id: 'tabGroup',
      header: 'Tab Group',
      sortable: true,
      cell: (_value: any, row: StatusDto) => (
        <div className="text-sm text-gray-700 dark:text-gray-300">{(row as any).tabGroup || '—'}</div>
      )
    },
    {
      id: 'description',
      header: 'Description',
      sortable: false,
      cell: (_value: any, row: StatusDto) => (
        <div className="text-sm text-gray-700 dark:text-gray-300">{row.description || '-'}</div>
      )
    },
    {
      id: 'sortOrder',
      header: 'Sort Order',
      sortable: true,
      cell: (_value: any, row: StatusDto) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{row.sortOrder}</div>
      )
    },
    {
      id: 'isActive',
      header: 'Active',
      sortable: true,
      cell: (_value: any, row: StatusDto) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.isActive 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      id: 'createdDate',
      header: 'Created Date',
      sortable: true,
      cell: (_value: any, row: StatusDto) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {new Date(row.createdDate).toLocaleDateString()}
            </div>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            <button 
              onClick={() => {
                setEditingStatus(row);
                setIsEditModalOpen(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit Status"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleDeleteClick(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete Status"
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
  ], []);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Status Management</h1>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium">Error loading statuses</div>
            <div className="text-gray-500 mt-2">Please try again</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ListOrdered className="h-8 w-8 text-gray-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Status Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage work order statuses</p>
          </div>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Status
        </Button>
      </div>

      <DataGrid
        data={paginatedStatuses}
        columns={columns}
        isLoading={isLoading}
        searchTerm={search}
        onSearch={handleSearch}
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        searchPlaceholder="Search statuses..."
        emptyMessage="No statuses found"
        getRowClassName={() => 'group'}
        onRowClick={(row) => {
          setEditingStatus(row as StatusDto);
          setIsEditModalOpen(true);
        }}
        rowTooltip="Edit Status"
      />

      {/* Create Modal */}
      <StatusModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
      />

      {/* Edit Modal */}
      <StatusModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingStatus(null);
        }}
        onSubmit={(data) => editingStatus && updateMutation.mutate({ id: editingStatus.id, data })}
        isLoading={updateMutation.isPending}
        status={editingStatus}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Status"
        message={`Are you sure you want to delete "${statusToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteMutation.isPending}
        variant="danger"
      />
    </div>
  );
};

export default StatusAdminPage;
